//GetSales.ts ==============================================================
import "../Util/Prototypes";

import * as Custom from "../Custom";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as GeoFence from "./GeoFence";
import * as GetSearchResults from "../Search/GetSearchResults";
import * as GraLay from "../Util/GraLay";
import * as MainMap from "../MainMap";
import * as SalesAverages from "./SalesAverages";
import * as SalesSearch from "./SalesSearch";
import * as SalesSearchSettings from "./SalesSearchSettings";
import * as Search from "../Search/Search";
import * as Settings from "../Settings";
import * as SnackBar from "../Util/SnackBar";
import * as Spinner from "../Util/Spinner";
import * as TimePeriod from "./TimePeriod";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as Widgets from "../Widgets/Widgets";

import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import Query from "@arcgis/core/rest/support/Query";
import * as QueryTask from "@arcgis/core/rest/query";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";

export let aveSalesPricePrompt: string;
export let pricePerSizePrompt: string;
export let adjustedPricePrompt: string;

export let aveSalesPriceTxt: string;
export let avePricePerSizeTxt: string;
export let adjustedPriceTxt: string;

//Setters.
export function setAveSalesPricePrompt(aveSalesPricePrompt_in: string) {
   aveSalesPricePrompt = aveSalesPricePrompt_in;
}

export function setPricePerSizePrompt(pricePerSizePrompt_in: string) {
   pricePerSizePrompt = pricePerSizePrompt_in;
}

export function setAdjustedPricePrompt(adjustedPricePrompt_in: string) {
   adjustedPricePrompt = adjustedPricePrompt_in;
}

export function setAveSalesPriceTxt(aveSalesPriceTxt_in: string) {
   aveSalesPriceTxt = aveSalesPriceTxt_in;
}

export function setAvePricePerSizeTxt(avePricePerSizeTxt_in: string) {
   avePricePerSizeTxt = avePricePerSizeTxt_in;
}

export function setAdjustedPriceTxt(adjustedPriceTxt_in: string) {
   adjustedPriceTxt = adjustedPriceTxt_in;
}

let fs: __esri.FeatureSet;
let ss: SalesSearchSettings.iSS; //Settings.
let propTypeSpec: SalesSearchSettings.iPropType;
let improveTypeSpec: SalesSearchSettings.iImproveType;
let omitImproveSpec: SalesSearchSettings.iOmitImproveTypeCriteria;

export function search() {
   if (Spinner.busy) {
      return; //Quit if busy.
   }
   Spinner.show();

   Util.logMsg("Top of Get Sales.search");
   SalesSearch.layer.totRec = 0;
   SalesSearch.layer.fs = null;

   //Get settings.
   ss = SalesSearchSettings.ss;
   propTypeSpec = SalesSearchSettings.getPropTypeSpec(SalesSearch.propertyType);
   improveTypeSpec = SalesSearchSettings.getImproveTypeSpec(SalesSearch.improvementType);
   omitImproveSpec = SalesSearchSettings.getOmitImproveTypeSpec(SalesSearch.improvementType);

   SalesSearch.layer.spec = Util_App.getLayerSpec(propTypeSpec.layerName);
   if (!SalesSearch.layer.spec) {
      Util.errMsg("Sales points layer not found: " + propTypeSpec.layerName);
      return;
   }

   //Create query task.
   const query = new Query({
      returnGeometry: true,
      outSpatialReference: MainMap.activeView.spatialReference,
      outFields: ["*"],
      geometryPrecision: 0,
   });

   if (GeoFence.geoFenceGeom) {
      query.geometry = GeoFence.geoFenceGeom;
   }

   const sql: string = buildWhereString();
   if (sql !== "") {
      query.where = sql;
   }
   Util.logMsg("Sales Search Where for " + SalesSearch.layer.spec.layerName + ":\n"  + sql);

   //Make sure query is not empty.
   if (!GeoFence.geoFenceGeom && sql === "") {
      query.where = "1=1";
   }

   const url: string = Util_App.getLayerURL(SalesSearch.layer.spec);
   QueryTask.executeQueryJSON(url, query).then(queryTask_success).catch(queryTask_failed);
}

//Sales points search completed event.
function queryTask_success(raw_fs: __esri.FeatureSet) {
   import("@arcgis/core/PopupTemplate").then(({ default: PopupTemplate }) => {

      Spinner.hide();

      SalesSearch.clearAllResults();
      fs = raw_fs;

      const layer: Search.iLayer = SalesSearch.layer;
      layer.name = SalesSearch.layer.spec.layerName;
      layer.fs = fs;
      layer.begRec = 0;
      layer.totRec = fs.features.length;
      layer.endRec = Math.min(layer.totRec, Settings.maxRecordCount) - 1;
      layer.numRec = Math.min(layer.totRec, Settings.maxSearchRecPerPage);
      layer.zoomAllMBR = Util_App.getMBRFeatureSet(fs);

      //No records found.
      if (layer.totRec === 0) {
         SnackBar.show("No sales found", 2);
         return;
      }

      //Remove unused fields for vacant properties.
      if (improveTypeSpec.vacant) {
         for (const fieldName of ss.omitVacantFields) {
            const i: number = Util_App.getFieldIndexByName(fieldName, fs);
            if (i !== -1) {
               fs.fields.RemoveAt(i);
            }
         }
      }

      //Add fields for processing match score.
      if (SalesSearch.activePIN) {
         fs.fields.InsertAt(0, { name: "Score", alias: "Score", type: "double" });
         fs.fields.InsertAt(1, { name: "Distance", alias: "Distance", type: "double" });

         for (const feature of fs.features) {
            feature.attributes.Score = 0;

            const gra = feature as any;
            gra.rowSelected = false; //Initialize selected rows.

            const pnt = feature.geometry as __esri.Point;
            const dist: number = pnt.distance(ss.subjectCentroid);

            //Convert feet to miles.
            if (MainMap.activeView.spatialReference.isWebMercator) {
               feature.attributes.Distance = Util.parseFlt((dist / 1609).toFixed(2));
            } else {
               feature.attributes.Distance = Util.parseFlt((dist / 5280).toFixed(2));
            }
         }

         //Computes and sorts the results by match score and computes sales averages.
         computeMatchScores();
      }

      //Add price per size to feature set results.
      addPricePerSize();

      //Set "Selected" values.
      if (SalesSearch.activePIN) {
         const minToSelect: number = Math.min(3, fs.features.length);

         for (let i: number = 0; i < minToSelect; i++) {
            const gra = fs.features[i] as any;
            gra.rowSelected = true;
         }
         //} else {
         //   for (const feature of fs.features) {
         //      feature.rowSelected = true;
         //   }
      }

      //Add graphic points to map.
      const sym = new PictureMarkerSymbol({ height: 18, width: 18, url: "https://static.arcgis.com/images/Symbols/" + propTypeSpec.image1 });

      for (const gra of fs.features) {
         //Popup title.
         const popupTemplate = new PopupTemplate();
         popupTemplate.title = layer.spec.layerName;
         popupTemplate.title += Custom.getCustomMapTipTitleHTML(layer.spec.layerName, gra, fs); //Append custom html.

         //Popup content.
         popupTemplate.content = Custom.getCustomMapTipContentHTML(layer.spec.layerName, gra, fs);
         if (!popupTemplate.content) {
            popupTemplate.content = GetSearchResults.getMapTipContentHTML(layer.spec.layerName, gra, fs);
         }

         //Add the graphic.
         gra.popupTemplate = popupTemplate;
         gra.symbol = sym;
         GraLay.appPnt.add(gra);
      }

      displayResults();

   }).catch((error: Error) => {
      Util.errMsg('An error occurred while loading the popup template module:', error);
   });
}

//Display results.
export function displayResults() {
   SalesAverages.computeSalesAves();
   addHighlightGraphics(); //Add yellow background highlight graphics.

   //Build left panel results.
   if (DrawerMenu.curToolName === "Sales Results") {
      Widgets.show("Sales Results");

   } else { //Sales search was the current tool.
      DrawerMenu.showTool("Sales Results");
   }
}

//Add highlight graphics to map for selected sales points.
export function addHighlightGraphics() {
   const sym = new SimpleMarkerSymbol({ color: [240, 255, 0, 0.8], size: 23, outline: { color: [255, 255, 255, 0.8] } });
   GraLay.appPntHighlight.removeAll();
   const gras: __esri.Graphic[] = [];

   for (const feature of fs.features) {
      const gra = feature as any;

      if (gra.rowSelected) {
         const gra2 = feature.clone();
         gra2.symbol = sym;
         gra2.attributes = null;
         gra2.popupTemplate = null;
         gras.push(gra2);
      }
   }

   if (gras) {
      GraLay.appPntHighlight.addMany(gras);
   }
}

//Add price per size to query task results.
function addPricePerSize() {
   let atrs: any;
   let sizeValue: number;
   let salePrice: number;

   let i: number = Util_App.getFieldIndexByName(ss.SalePrice.field, fs) + 1;

   if (!improveTypeSpec.vacant || improveTypeSpec.name === "Any") {
      fs.fields.InsertAt(i, { name: "pricePerSqFt", alias: "Price Per SqFt(Money)", type: "double" });
      i++;

      for (const feature of fs.features) {
         atrs = feature.attributes;
         salePrice = atrs[ss.SalePrice.field];
         sizeValue = atrs[ss.StructureSize.field];
         if (sizeValue > 0) {
            atrs.pricePerSqFt = Util.parseFlt((salePrice / sizeValue).toFixed(2));
         }
      }
   }

   if (improveTypeSpec.vacant || improveTypeSpec.name === "Any") {
      fs.fields.InsertAt(i, { name: "pricePerAcre", alias: "Price Per Acre(Money)", type: "double" });

      for (const feature of fs.features) {
         atrs = feature.attributes;
         salePrice = atrs[ss.SalePrice.field];
         sizeValue = atrs[ss.LotSize.field];

         if (ss.LotSize.convertSalesToAcres) {
            sizeValue = Util.parseFlt(Util.formatTextValue(sizeValue, "Acres"));
         }
         if (sizeValue > 0) {
            atrs.pricePerAcre = Util.parseFlt((salePrice / sizeValue).toFixed(2));
         }
      }
   }
}

//Compute the comparable property score.  Custom!
function computeMatchScores() {
   const maxScorePoints = 45;
   let percent: number;
   let score: number;
   let delta: number;
   let rangeFac: number;

   //Compute max distance away from subject property.
   let maxDistance = 0;
   for (const feature of fs.features) {
      maxDistance = Math.max(maxDistance, feature.attributes.Distance);
   }

   //Compute from sale date age in months.
   let fromSaleAgeMonths: number = 120;
   const today: Date = new Date();

   if (TimePeriod.fromDateYMD !== "") {
      const fromDate: Date = new Date(TimePeriod.fromDateYMD);
      fromSaleAgeMonths = Util.dateDiffInMonths(today, fromDate);
   }

   //Process all sales records.
   for (const feature of fs.features) {
      const atrs: any = feature.attributes;

      let rawScore: number = 0;
      let numRawScore: number = 0;

      //Process all scoring criteria.
      for (const key of propTypeSpec.criteriaKeys) {
         if (omitImproveSpec && omitImproveSpec.criteriaKeys.Contains(key)) {
            continue;
         }

         const criteria: SalesSearchSettings.iCriteria = ss[key];
         const subjectVal: number = criteria.subjectValue;
         let salesVal: number = atrs[criteria.field];

         if (key === "StructureSize" || key === "BasementSqFt") {
            if (subjectVal > 0) {
               rangeFac = 1; //Make larger to increase the range of values that score points.

               delta = Math.min(Math.abs(salesVal - subjectVal), subjectVal * rangeFac);
               percent = 1 - (delta / (subjectVal * rangeFac));
               score = percent * maxScorePoints;

               rawScore += score * criteria.weighting;
               numRawScore += criteria.weighting;
            }

         } else if (key === "LotSize") {
            if (subjectVal > 0) {
               if (criteria.convertSalesToAcres) {
                  salesVal = parseFloat(Util.formatTextValue(salesVal, "Acres"));
               }

               rangeFac = 3;
               if (improveTypeSpec.vacant) {
                  rangeFac = 2;
               }

               delta = Math.min(Math.abs(salesVal - subjectVal), subjectVal * rangeFac);
               percent = 1 - (delta / (subjectVal * rangeFac));
               score = percent * maxScorePoints;

               rawScore += score * criteria.weighting;
               numRawScore += criteria.weighting;
            }

         } else if (key === "YearBuilt") {
            if (subjectVal > 0 && salesVal > 0) {
               const subjectAge: number = today.getFullYear() - subjectVal;
               const saleAge: number = today.getFullYear() - salesVal;

               rangeFac = 3.5; //3 = 30, 4 = 40 year range gets scoring points.  Increase for larger range.

               delta = Math.min(Math.abs(saleAge - subjectAge), subjectAge * rangeFac);
               percent = 1 - (delta / (subjectAge * rangeFac));
               score = percent * maxScorePoints;

               rawScore += score * criteria.weighting;
               numRawScore += criteria.weighting;
            }

         } else if (key === "Bedrooms" || key === "Bathrooms" || key === "Condition" || key === "Quality") {
            if (subjectVal > 0 && salesVal > 0) {
               rangeFac = 0.5;

               delta = Math.min(Math.abs(salesVal - subjectVal), subjectVal * rangeFac);
               percent = 1 - (delta / (subjectVal * rangeFac));
               score = percent * maxScorePoints;

               rawScore += score * criteria.weighting;
               numRawScore += criteria.weighting;
            }

         }
      } //Next criteria

      //Process distance criteria.
      if (maxDistance > 0) {
         rangeFac = 1.5;

         percent = 1 - (atrs.Distance / (maxDistance * rangeFac));
         score = percent * maxScorePoints;

         rawScore += score * ss.distanceWeighting;
         numRawScore += ss.distanceWeighting;
      }

      //Process sale date criteria.
      const saleDateValue: string = atrs[ss.saleDate.field]; //In seconds.

      if (saleDateValue) {
         const saleDate: Date = new Date(saleDateValue);
         const saleAgeMonths: number = Util.dateDiffInMonths(today, saleDate);

         rangeFac = 2; //2 = 50% of points for oldest date.

         percent = 1 - (saleAgeMonths / (fromSaleAgeMonths * rangeFac));
         score = percent * maxScorePoints;

         rawScore += score * ss.saleDate.weighting;
         numRawScore += ss.saleDate.weighting;
      }

      //Compute total score for this record.
      atrs.Score = 0;
      if (numRawScore > 0) {
         const scoreOffset = 100 - maxScorePoints - 1;
         atrs.Score = parseFloat(((rawScore / numRawScore) + scoreOffset).toFixed(1));
      }
   } //Next sales record.

   //Sort by match score.
   fs.features.sort(Util_App.sortFC_Reverse("Score"));
}

//Query task failed.
function queryTask_failed(error: Error): void {
   Spinner.hide();
   Util.errMsg("Query Task failed", error);
}

function buildWhereString() {
   let sql: string = "";
   let value: any;

   //Property type.
   if (SalesSearch.propertyType !== "Any") {
      sql += propTypeSpec.where;
   }

   //Improvement type.
   if (SalesSearch.improvementType !== "Any") {
      if (improveTypeSpec.where !== "") {
         if (sql !== "") {
            sql += " AND ";
         }
         sql += improveTypeSpec.where;
      }
   }

   //Sale date.
   sql += buildWhereLine(sql, ss.saleDate.field, "Date", TimePeriod.fromDateYMD, "Min");
   sql += buildWhereLine(sql, ss.saleDate.field, "Date", TimePeriod.toDateYMD, "Max");

   //Min max search criteria.
   for (const criteriaKey of propTypeSpec.criteriaKeys) {
      if (omitImproveSpec && omitImproveSpec.criteriaKeys.Contains(criteriaKey)) {
         continue;
      }

      //Min-max values.
      const criteria: SalesSearchSettings.iCriteria = ss[criteriaKey];
      const criteriaDropdown: SalesSearchSettings.iCriteriaDropdown = ss[criteriaKey];

      if (criteria && (typeof criteria.weighting !== "undefined")) {

         //Min value.
         value = criteria.minValue;
         if (value > 0) {
            if (criteria.convertSalesToAcres) {
               value = Util.formatTextValue(value, "SqFt"); //convert to square feet.
            }
            sql += buildWhereLine(sql, criteria.field, "Number", value, "Min");
         }

         //Max value.
         value = criteria.maxValue;
         if (value > 0) {
            if (criteria.convertSalesToAcres) {
               value = Util.formatTextValue(value, "SqFt"); //convert to square feet.
            }
            sql += buildWhereLine(sql, criteria.field, "Number", value, "Max");
         }

      } else { //Dropdown criteria.
         value = criteriaDropdown.value;
         if (criteriaDropdown && value !== "Any") {
            sql += buildWhereLine(sql, criteriaDropdown.field, "Txt", value);
         }

      }
   }

   return sql;
}

//Set where string for a single field.
function buildWhereLine(inWhere: string, fieldName: string, dataType: string, fieldValue: any, minMax?: string) {
   let sql: string = "";

   if (fieldName !== "" && fieldValue !== "") {
      if (inWhere !== "") {
         sql += " AND ";
      }

      if (dataType === "Number") {
         if (minMax === "Min") {
            sql += fieldName + " >= " + fieldValue;
         } else if (minMax === "Max") {
            sql += fieldName + " <= " + fieldValue;
         } else {
            sql += fieldName + " = " + fieldValue;
         }

      } else if (dataType === "Date") {
         if (minMax === "Min") {
            sql += fieldName + " >= timestamp '" + fieldValue + "'";
         } else if (minMax === "Max") {
            sql += fieldName + " <= timestamp '" + fieldValue + "'";
         } else {
            sql += fieldName + " = timestamp '" + fieldValue + "'";
         }

      } else if (dataType === "Txt") {
         sql += fieldName + " = '" + fieldValue + "'";

      } else if (dataType === "Contains") {
         sql += fieldName + " Like '%" + fieldValue + "%'";
      }
   }
   return sql;
}
